import { FGCustomPanel, FGListen, FGTextInput, FieldGroup } from "nsitools-react";
import * as React from "react";
import { useHistory, useParams } from "react-router";
import * as Yup from "yup";

import { FcbFormateurTitreDtoFromJSON, FichierApi, FormateurTitreApi } from "../../../../../../api";
import { ERoutes } from "../../../../../../AppRouter";
import {
  FGWalterDateMaskInput,
  FGWalterFileInput,
  FGWalterSelectInput,
  SmallFormGenerator
} from "../../../../../../components";
import { useApiService, useCrudApi, useTl } from "../../../../../../hooks";
import { useReferential } from "../../../../../../hooks/useReferential";
import { ETLCodes } from "../../../../../../locales";

export interface IActiviteProfessionnelleTitrePageProps {}

export const ActiviteProfessionnelleTitrePage: React.FunctionComponent<IActiviteProfessionnelleTitrePageProps> = () => {
  const { id, idTitreFormateur, state } = useParams<{ id: string; idTitreFormateur: string; state: string }>();
  const { t } = useTl();
  const api = useApiService(FormateurTitreApi);
  const history = useHistory();

  const [titre, loadingTitre] = useReferential(a => a.referentialGetTitresPedagogiques(), true, []);

  const FormSchema = React.useMemo(() => {
    return Yup.object().shape({
      idtitre: Yup.number().required(t(ETLCodes.Required)),
      date: Yup.date()
    });
  }, [t]);

  const { data, loading, deleteItem, deleting, saveItem, saving } = useCrudApi({
    getApiFn: () =>
      +idTitreFormateur <= 0
        ? FcbFormateurTitreDtoFromJSON({ idformateur: +id })
        : api.formateurTitreGet({ id: +idTitreFormateur }),
    saveApiFn: d => api.formateurTitreSave({ FcbFormateurTitreDto: d }),
    onSavedRoute: d => `${ERoutes.formateur}/${d.idformateur}/activitesProfessionnelles`,
    deleteApiFn: d => api.formateurTitreDelete({ id: d.idtitreFormateur }),
    onDeletedRoute: () => `${ERoutes.formateur}/${+id}/activitesProfessionnelles`
  });

  const fapi = useApiService(FichierApi);
  const downloadFn = React.useCallback(async (idfichier: number) => await fapi.fichierDownload({ id: idfichier }), [
    fapi
  ]);

  return (
    data && (
      <SmallFormGenerator
        initialValues={data}
        onSubmit={saveItem}
        showColons
        editMode={state === "edit"}
        minLabelWidth={180}
        labelAlignment="right"
        formatDate="dd/MM/yyyy"
        validationSchema={FormSchema}
        inline
        boldLabel
        loading={loading}
        onCancel={() => history.push(`${ERoutes.formateur}/${+id}/activitesProfessionnelles`)}
        onDelete={deleteItem}
        showDeleteButton={+idTitreFormateur > 0}
        saving={saving}
        deleting={deleting}
      >
        <FGListen
          field="fichier"
          onChanged={(value, formik) => {
            if (formik.dirty) {
              formik.setFieldValue("idfichier", null);
            }
          }}
        />
        <FieldGroup columns={1}>
          <FGWalterSelectInput name="idtitre" label={t(ETLCodes.Titre)} items={titre} loading={loadingTitre} />
          <FGWalterDateMaskInput name="date" label={t(ETLCodes.Date)} />
          <FGTextInput name="libelle" label={t(ETLCodes.Libelle)} maxLength={50} />
          <FGCustomPanel>
            {({ formik }) => (
              <FGWalterFileInput
                name="fichier"
                label={t(ETLCodes.Document)}
                downloadFn={formik.values?.idfichier && (() => downloadFn(formik.values?.idfichier))}
              />
            )}
          </FGCustomPanel>
        </FieldGroup>
      </SmallFormGenerator>
    )
  );
};
